import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesCalendarMessages } from "@/i18n/messages/frames-calendar";
import { cn } from "@/lib/cn";
import { SkeletonReveal, Spinner } from "@/components/ui";
import { PreviewBody } from "@/components/shapes/shared/card-preview-parts";
import {
  DayAccent,
  EventRows,
} from "@/components/shapes/shared/card-visual-parts";
import { CARD_BODY_CONTENT } from "@/components/shapes/shared/frame-content-padding";
import { useFrameSurface } from "../shared/use-frame-surface";
import { calendarRows, todayAccent, weekdayLabel } from "./calendar-events";

interface WeekDay {
  label: string;
  date: string;
  isToday: boolean;
}

/** The current Mon–Sun week (local time), in the app locale. */
function currentWeek(now: Date): WeekDay[] {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);
    return {
      label: weekdayLabel(day),
      date: `${day.getDate()}`,
      isToday: day.toDateString() === now.toDateString(),
    };
  });
}

/** The week under today's rows: neutral days, today's weekday in the date accent. */
function WeekStrip({ week }: { week: readonly WeekDay[] }) {
  return (
    <div className="flex gap-[0.4em]" data-testid="calendar-frame-week-strip">
      {week.map((day) => (
        <div
          key={day.date}
          className="flex-1 rounded-[0.6em] bg-[var(--card-line)] py-[0.4em] text-center text-[0.8em] tabular-nums"
          data-today={day.isToday || undefined}
        >
          <p
            className={cn(
              "font-semibold",
              day.isToday
                ? "text-[color:var(--gadget-date)]"
                : "text-card-muted",
            )}
          >
            {day.label}
          </p>
          <p className="text-card-ink">{day.date}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * A calendar, opened as its card (Part 10): today's date accent and its
 * coloured-dot rows with their details, then the week strip. Read-only.
 * Content renders from data.content ({events[]}); agent frame.update
 * broadcasts re-render it.
 */
export function CalendarFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesCalendarMessages);
  const frameSurface = useFrameSurface(id);
  const rows = calendarRows(data.content?.events);
  const status = data.status;
  const isEmpty = rows.length === 0;
  const now = new Date();

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="calendar-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none min-h-0 flex-1 overflow-y-auto",
          CARD_BODY_CONTENT,
          selected && "nowheel",
        )}
        data-frame-content="true"
        data-frame-drag-handle="true"
        onClick={(e) => e.stopPropagation()}
      >
        <PreviewBody density="regular" className={cn(isEmpty && "h-full")}>
          <SkeletonReveal
            sizeToContent
            loading={status === "streaming" && isEmpty}
            loadingLabel={t("loadingCalendar")}
            data-testid="calendar-frame-reveal"
            contentClassName="gap-[0.9em]"
            skeleton={
              <div
                data-testid="calendar-frame-loading"
                aria-label={t("loadingCalendar")}
              >
                <div className="mb-2 flex items-center gap-2">
                  <Spinner className="h-3 w-3" />
                  <span className="text-appkit-small text-muted">
                    {t("syncingCalendar")}
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-1" aria-hidden="true">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-10 bg-glass-fill-dense rounded-control"
                    />
                  ))}
                </div>
                <div className="mt-2 flex flex-col gap-1.5" aria-hidden="true">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-9 bg-glass-fill-dense rounded-control"
                    />
                  ))}
                </div>
              </div>
            }
          >
            {isEmpty ? (
              <FrameEmptyState
                frameId={id}
                status={status}
                testId="calendar-frame-empty"
              >
                {t("noEvents")}
              </FrameEmptyState>
            ) : (
              <>
                <DayAccent {...todayAccent(now)} />
                <EventRows rows={rows} details testId="calendar-frame-event" />
                <WeekStrip week={currentWeek(now)} />
              </>
            )}
          </SkeletonReveal>
        </PreviewBody>
      </div>
    </div>
  );
}
